import React from "react";
import { Box, Button, Flex, Portal, Text, useBreakpointValue } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { isCovered, layoutViewportSize } from "../../lib/coverage";

// 스포트라이트 코치마크 — 화면을 어둡게 하고 대상 요소 하나만 밝혀 「여기를 누르라」를 가리킨다.
// 대상은 `data-guide="<anchor>"` 속성으로 찾는다(클래스·DOM 구조에 의존하지 않는다).
//
// - blocking=true  : 대상 밖은 4개의 가림막이 클릭을 막는다(대상 영역만 뚫려 있어 실제 요소를 그대로 누른다)
// - blocking=false : 가림막 없이 링 + 말풍선만 — 대상 주변을 조작해야 열리는 버튼(상세의 CTA)에 쓴다
// - md 이상은 말풍선(대상 오른쪽 → 아래 → 위 순으로 자리를 찾는다), 그 미만은 바텀시트
// - 대상이 아직 없으면(모달 마운트 중) 짧게 기다렸다가 그려지고, 끝내 없으면 아무것도 그리지 않는다(fail-open)
// - 대상이 **다른 층에 가려져 있으면**(모달·드로어·시트가 위에 떠 있어 대상 중심을 눌러도 대상이 안 눌리는 상태) 그리지 않고 기다린다 —
//   2026-09-24 실측: 채널 등록 모달이 열린 채로 뒤쪽 번들 카드의 코치마크가 모달 위에 떠 있었다. `isCovered()` 가 300ms 마다 다시 본다.
// - ESC = 건너뛰기. 열리면 첫 버튼으로 포커스.
//
// z-index 는 Chakra 모달(1400) 위, 토스트(5500) 아래 — 모달 **안**의 대상을 가리킬 수 있어야 해서다. 모달 **뒤**의 대상은 위 규칙으로 숨긴다.

const Z = 1450;
const PAD = 6;
const TOOLTIP_W = 336;
const GAP = 16;
const ARROW = 16;        // 말꼬리(45° 회전 정사각형) 한 변
const ARROW_INSET = 20;  // 말꼬리가 말풍선 모서리(둥근 14px) 안쪽에 머물도록 두는 최소 여백
const DIM = "rgba(17, 24, 39, 0.6)";

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 4px #fff, 0 0 0 8px rgba(60, 183, 160, 0.55); }
  100% { box-shadow: 0 0 0 4px #fff, 0 0 0 14px rgba(60, 183, 160, 0); }
`;
const pop = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

function findAnchor(anchor) {
  if (!anchor || typeof document === "undefined") return null;
  return document.querySelector(`[data-guide="${anchor}"]`);
}

// 세로·가로 모두 본다(2026-09-27 — 가로로 화면 밖인 대상은 자동 스크롤도 안 되고 isCovered 로 계속 숨어 있었다)
export function isOffscreen(r) {
  const vp = layoutViewportSize(); // 확대(핀치) 중에도 레이아웃 좌표계로 — src/lib/coverage.js
  return r.top < 0 || r.bottom > vp.height || r.left < 0 || r.right > vp.width;
}

// 사용자가 직접 스크롤했다고 보는 입력 — 휠·터치 끌기·스크롤 키·누르기(스크롤바 끌기 포함).
// 휠·터치 끌기는 어디서 일어나든 스크롤이다 — 어둡게 덮는 층이 화면 대부분을 덮어서, 그 위에서 굴려도 페이지가 움직인다
// (2026-09-27 시뮬레이션: 층 위 휠을 빼자 자동 스크롤이 사용자를 대상으로 끌어당겼다).
// 스크롤 키(PageDown·화살표·Home·End·Space)는 코치마크 버튼에 포커스가 있어도 페이지를 움직이므로 센다 — 코치마크가 첫 버튼에
// 포커스를 주기 때문에 층 안 키를 빼면 키보드 사용자의 스크롤이 무시되고 자동 스크롤이 끌어당겼다(2026-09-27 재리뷰 2회).
// 스크롤이 아닌 것: 코치마크 층 안의 누르기, 입력칸 타자, 버튼 위 Space(누르기), 스크롤 키가 아닌 키.
// 포커스 이동(Shift+Tab 으로 페이지 끝 요소로·화면 낭독기의 이동·폼 오류 칸으로)은 **그 포커스 뒤에 실제로 스크롤이 일어났을 때만**
// 센다 — 층 밖 focusin 이 있고 FOCUS_SCROLL_WINDOW_MS 안에 스크롤 이벤트(페이지든 모달 본문 같은 스크롤 상자든)가 오면 사용자 이동.
// 브라우저마다·설정마다 순서가 달라서다: 스크롤이 즉시면 focusin 전에 이미 끝나 있고(크로미움 실측), 이 앱처럼
// `scroll-behavior: smooth`(src/index.css)면 focusin 뒤에 시작한다 — 어느 쪽이든 스크롤 이벤트는 focusin 직후 프레임에 온다.
// Chakra 모달·드로어가 열릴 때의 자동 포커스, 닫힐 때 포커스 돌려주기처럼 스크롤 없는 포커스는 세지 않는다
// (재리뷰 3~7회: Tab 여부·도착 여부·포커스 요소 위치·대상 위치 비교는 모두 한쪽 경우를 놓쳤다).
const USER_SCROLL_EVENTS = ["wheel", "touchmove", "keydown", "pointerdown"];
export const FOCUS_SCROLL_WINDOW_MS = 200;
// 층 밖 포커스(focusAt) 뒤 창 안에 온 스크롤이면 사용자 이동 — 코치마크 자신의 자동 스크롤 중이면 아니다
export function isFocusDrivenScroll({ focusAt = 0, now = Date.now(), autoScrolling = false } = {}) {
  return !autoScrolling && focusAt > 0 && now - focusAt <= FOCUS_SCROLL_WINDOW_MS;
}
const SCROLL_KEYS = new Set(["PageUp", "PageDown", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End", " ", "Spacebar"]);
const closestOf = (t, sel) => (t && typeof t.closest === "function" ? t.closest(sel) : null);
export function isUserScrollIntent(e) {
  const t = e && e.target;
  if (e.type === "wheel" || e.type === "touchmove") return true;
  const inLayer = !!closestOf(t, "[data-coachmark-layer]");
  if (e.type === "keydown") {
    // Tab 자체는 세지 않는다 — 포커스가 옮겨 가 실제로 스크롤되면 「포커스 뒤 스크롤」(isFocusDrivenScroll)이 센다
    if (!SCROLL_KEYS.has(e.key)) return false;
    if (closestOf(t, "input, textarea, select, [contenteditable=''], [contenteditable='true']")) return false;
    // 버튼 위 Space 는 누르기다. 링크는 Enter 로만 눌리고 Space 는 페이지를 스크롤하므로 빼지 않는다
    if ((e.key === " " || e.key === "Spacebar") && closestOf(t, "button, [role='button'], summary")) return false;
    return true;
  }
  if (e.type === "pointerdown") return !inLayer;
  return false; // focusin·scroll 등 — 포커스 이동은 isFocusDrivenScroll 이 따로 본다
}
const MAX_AUTO_SCROLLS = 3;
const AUTO_SCROLL_SETTLE_MS = 1500; // 부드러운 스크롤이 끝나기를 기다리는 시간

// 대상이 가려졌는가 — 판정은 src/lib/coverage.js 한 곳(ⓘ 팝오버·어드민 말풍선과 같은 규칙). 예전 import 경로를 위해 다시 내보낸다.
// 대상 중심이 화면 밖이면 「가려짐」(2026-09-27 말풍선 전수조사) — 사용자가 스크롤로 대상을 떠나면 말풍선·시트를 숨긴다.
export { isCovered };

// 대상 요소의 위치를 추적한다 — 찾을 때까지 재시도(최대 3초), 이후 스크롤·리사이즈·주기 재측정.
function useAnchorRect(anchor, isOpen) {
  const [rect, setRect] = React.useState(null);
  React.useEffect(() => {
    if (!isOpen || !anchor) {
      setRect(null);
      return undefined;
    }
    let raf = 0;
    let tries = 0;
    // 자동 스크롤: 대상이 화면 밖이면 데려온다 — 단 사용자가 직접 스크롤했으면 끌어당기지 않는다(2026-09-27 최종 QA).
    // 사용자 입력 없이 대상이 밀려 나간 경우(위쪽 내용이 늦게 로드되는 등)는 다시 데려온다. 무한 반복을 막으려고 최대 3번.
    let autoScrolls = 0;
    let autoScrollAt = 0; // 부드러운 스크롤이 도는 동안(대상이 아직 화면 밖) 같은 자동 스크롤을 또 부르지 않게
    // 마지막 자동 스크롤 시작 시각 — 도착해도 지우지 않는다. 부드러운 스크롤은 대상이 화면에 들어온 뒤에도 가운데까지 계속 움직이는데,
    // 그 사이의 스크롤을 「포커스 뒤 스크롤」로 세면 안 된다(재리뷰 8회: 모달 포커스 잠금이 포커스를 되돌리며 오판 → 다시 데려오기 꺼짐)
    let autoScrollStartedAt = 0;
    let userScrolled = false;
    let alive = true;
    let focusAt = 0; // 마지막 층 밖 포커스 시각 — 그 직후의 스크롤은 포커스가 일으킨 사용자 이동
    const markUser = (e) => {
      // Tab 은 사용자가 직접 포커스를 옮기는 것 — 자동 스크롤 직후라도 그 뒤 스크롤은 포커스가 일으킨 사용자 이동으로 센다(재리뷰 9회).
      // 모달 포커스 잠금이 포커스를 되돌리는 경우에는 Tab 이 없으므로 여전히 세지 않는다.
      if (e.type === "keydown" && e.key === "Tab") autoScrollStartedAt = 0;
      if (isUserScrollIntent(e)) userScrolled = true;
    };
    const onFocusIn = (e) => {
      const t = e.target;
      if (closestOf(t, "[data-coachmark-layer]")) return;
      focusAt = Date.now();
    };
    const measure = () => {
      if (!alive) return;
      const el = findAnchor(anchor);
      if (!el) {
        setRect(null);
        if (tries++ < 60) raf = requestAnimationFrame(measure); // ~1초(60fps) — 모달 마운트 대기
        return;
      }
      const r = el.getBoundingClientRect();
      const offscreen = isOffscreen(r);
      if (!offscreen) autoScrollAt = 0; // 도착했다
      const scrolling = autoScrollAt > 0 && Date.now() - autoScrollAt < AUTO_SCROLL_SETTLE_MS;
      if (offscreen && !userScrolled && !scrolling && autoScrolls < MAX_AUTO_SCROLLS) {
        autoScrolls += 1;
        autoScrollAt = Date.now();
        autoScrollStartedAt = autoScrollAt;
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
        raf = requestAnimationFrame(measure);
        return;
      }
      // 사용자가 스크롤로 떠났으면 isCovered(화면 밖 = 가려짐)가 코치마크를 숨긴다.
      if (isCovered(el, r)) {
        setRect(null); // 모달 등이 위에 떠 있다 — 닫히면 주기 재측정이 다시 그린다
        return;
      }
      setRect((prev) => (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height ? prev : { top: r.top, left: r.left, width: r.width, height: r.height }));
    };
    measure();
    const onChange = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    const onScroll = () => {
      const autoScrolling = autoScrollStartedAt > 0 && Date.now() - autoScrollStartedAt < AUTO_SCROLL_SETTLE_MS;
      if (isFocusDrivenScroll({ focusAt, autoScrolling })) userScrolled = true;
      onChange();
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("focusin", onFocusIn, true);
    window.addEventListener("resize", onChange);
    USER_SCROLL_EVENTS.forEach((t) => window.addEventListener(t, markUser, { capture: true, passive: true }));
    const iv = setInterval(measure, 300); // 모달 안 스크롤·레이아웃 변화 보정
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      clearInterval(iv);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("focusin", onFocusIn, true);
      window.removeEventListener("resize", onChange);
      USER_SCROLL_EVENTS.forEach((t) => window.removeEventListener(t, markUser, { capture: true }));
    };
  }, [anchor, isOpen]);
  return rect;
}

function StepBadge({ index, count, badge }) {
  return (
    <Flex align="center" justify="space-between">
      <Box bg="brand.50" color="brand.700" fontSize="12px" fontWeight="700" px={2.5} py={1} borderRadius="999px">
        {badge || `STEP ${index + 1} / ${count}`}
      </Box>
      {count > 1 && (
        <Flex gap="5px">
          {Array.from({ length: count }).map((_, i) => (
            <Box key={i} w="6px" h="6px" borderRadius="3px" bg={i === index ? "brand.500" : "neutral.300"} />
          ))}
        </Flex>
      )}
    </Flex>
  );
}

function Body({ body, strong }) {
  if (!strong || !body.includes(strong)) return <>{body}</>;
  const [before, after] = body.split(strong);
  return (
    <>
      {before}
      <Text as="strong" color="brand.700" fontWeight="700">{strong}</Text>
      {after}
    </>
  );
}

function Actions({ primary, secondary, firstRef, column = false }) {
  return (
    <Flex justify="flex-end" gap={2} mt={1} direction={column ? "column" : "row"}>
      {column && primary && (
        <Button ref={firstRef} colorScheme="brand" h="48px" fontSize="15px" fontWeight="700" borderRadius="10px" onClick={primary.onClick}>
          {primary.label}
        </Button>
      )}
      {(secondary || []).map((s, i) => (
        <Button
          key={s.label}
          ref={!primary && i === 0 ? firstRef : undefined}
          variant="ghost"
          color="neutral.500"
          h={column ? "44px" : "40px"}
          fontSize={column ? "15px" : "14px"}
          fontWeight="600"
          borderRadius="8px"
          flex={column ? undefined : "0 0 auto"}
          onClick={s.onClick}
        >
          {s.label}
        </Button>
      ))}
      {!column && primary && (
        <Button ref={firstRef} colorScheme="brand" h="40px" px={5} fontSize="14px" fontWeight="700" borderRadius="8px" onClick={primary.onClick}>
          {primary.label}
        </Button>
      )}
    </Flex>
  );
}

export default function SpotlightCoachmark({
  isOpen,
  anchor,
  stepIndex = 0,
  stepCount = 1,
  badge = null,        // 1단계 안내에서 STEP 대신 보여 줄 라벨("NEW" 등)
  title,
  body,
  strong,
  primary = null,      // { label, onClick }
  secondary = [],      // [{ label, onClick }]
  onSkip,              // ESC
  blocking = true,
  placement = "right", // 'right' | 'bottom' | 'top' (md 이상에서 첫 후보)
}) {
  const rect = useAnchorRect(anchor, isOpen);
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const firstRef = React.useRef(null);
  const [sheetH, setSheetH] = React.useState(0);
  const sheetRef = React.useCallback((node) => { if (node) setSheetH(node.getBoundingClientRect().height); }, []);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && onSkip) { e.stopPropagation(); onSkip(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen, onSkip]);

  // 첫 버튼 포커스 규칙(재리뷰 8·9회)
  // · 단계마다 처음 보일 때: 가리는(blocking) 안내는 가져가고, 가리지 않는 안내는 아무 데도 포커스가 없을 때(body)만 가져간다 —
  //   비모달 안내가 사용자가 연 ⓘ 말풍선의 포커스를 가져가 말풍선이 닫혔다(시뮬레이션 재현).
  // · 숨었다가(화면 밖·가려짐) 다시 보일 때: 아무 데도 포커스가 없을 때만 되돌려 준다 — 숨으면서 버튼이 사라져 포커스가 body 로
  //   떨어진 키보드 사용자가 다시 찾아가지 않아도 되게, 그러나 사용자가 다른 곳에 둔 포커스는 빼앗지 않게.
  // · 위치만 바뀔 때는 주지 않는다 — 바뀔 때마다 주면 Shift+Tab 으로 옮긴 포커스를 빼앗고 모달 포커스 잠금과 빼앗기를 반복했다.
  // 포커스를 못 받은 안내도 대상 요소의 aria-describedby 로 읽힌다(아래).
  const focusedStepRef = React.useRef(null);
  const visible = !!rect;
  React.useEffect(() => {
    if (!isOpen) {
      focusedStepRef.current = null;
      return undefined;
    }
    if (!visible) return undefined;
    const key = String(stepIndex ?? "");
    const firstShow = focusedStepRef.current !== key;
    // 말풍선은 Portal 이라 버튼이 한 박자 늦게 붙는다 — 붙을 때까지 몇 프레임 다시 본다
    let tries = 0;
    let timer = 0;
    const tryFocus = () => {
      if (firstRef.current) {
        focusedStepRef.current = key;
        const active = typeof document !== "undefined" ? document.activeElement : null;
        const idle = !active || active === document.body || active === document.documentElement;
        if (!idle && !(firstShow && blocking)) return;
        try { firstRef.current.focus({ preventScroll: true }); } catch { /* 무시 */ }
      } else if (tries++ < 10) {
        timer = setTimeout(tryFocus, 16);
      }
    };
    tryFocus();
    return () => clearTimeout(timer);
  }, [isOpen, visible, stepIndex, blocking]);

  // 3) 대상 요소에 안내 문장을 연결한다 — 포커스를 가져가지 못한 안내(모달이 포커스를 잡고 있을 때 등)도 사용자가 대상에
  //    닿으면 화면 낭독기가 제목·본문을 읽는다. 닫히거나 숨으면 원래 값으로 되돌린다(재리뷰 9회).
  const descId = React.useId();
  React.useEffect(() => {
    if (!isOpen || !visible) return undefined;
    const el = findAnchor(anchor);
    if (!el) return undefined;
    const prev = el.getAttribute("aria-describedby");
    const ids = `${descId}-title ${descId}-body`;
    el.setAttribute("aria-describedby", prev ? `${prev} ${ids}` : ids);
    return () => {
      if (prev === null) el.removeAttribute("aria-describedby");
      else el.setAttribute("aria-describedby", prev);
    };
  }, [isOpen, visible, anchor, descId]);

  if (!isOpen || !rect) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hole = { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 };

  // 말풍선 자리 — 요청 방향부터 시도하고 안 맞으면 아래 → 위 → 오른쪽 순
  // 말꼬리는 말풍선이 화면 안으로 밀려 대상과 어긋나도 항상 대상의 가운데를 가리키도록,
  // 최종 말풍선 좌표를 기준으로 다시 계산한다(고정 28px/34px 이면 우측 끝 대상(햄버거 메뉴)에서 왼쪽을 가리켰다).
  const tooltipStyle = (() => {
    const fitsRight = hole.left + hole.width + GAP + TOOLTIP_W <= vw - 8;
    const anchorCX = hole.left + hole.width / 2;
    const anchorCY = hole.top + hole.height / 2;
    const clampLeft = (l) => Math.max(8, Math.min(l, vw - TOOLTIP_W - 8));
    const arrowX = (left) => Math.max(ARROW_INSET, Math.min(anchorCX - left - ARROW / 2, TOOLTIP_W - ARROW_INSET - ARROW));
    // 오른쪽 배치는 말풍선 위가 대상 위와 나란하므로 세로는 대상 가운데(작은 대상)~34px(큰 대상) 사이
    const arrowY = (top) => Math.max(ARROW_INSET, Math.min(anchorCY - top - ARROW / 2, 34));
    const order = [placement, "bottom", "top", "right"];
    for (const p of order) {
      if (p === "right" && fitsRight) {
        const top = Math.max(8, Math.min(hole.top, vh - 260));
        return { top, left: hole.left + hole.width + GAP, arrow: "left", arrowOffset: arrowY(top) };
      }
      if (p === "bottom" && hole.top + hole.height + GAP + 220 <= vh) {
        const left = clampLeft(hole.left);
        return { top: hole.top + hole.height + GAP, left, arrow: "top", arrowOffset: arrowX(left) };
      }
      if (p === "top" && hole.top - GAP - 220 >= 0) {
        const left = clampLeft(hole.left);
        return { bottom: vh - hole.top + GAP, left, arrow: "bottom", arrowOffset: arrowX(left) };
      }
    }
    const left = clampLeft(hole.left);
    return { top: Math.min(hole.top + hole.height + GAP, vh - 240), left, arrow: "top", arrowOffset: arrowX(left) };
  })();

  // 모바일 시트가 대상을 덮으면 위쪽에 붙인다
  const sheetAtTop = isMobile && sheetH > 0 && hole.top + hole.height > vh - sheetH - 8;

  const shade = (style) => (
    <Box position="fixed" bg={DIM} zIndex={Z} data-coachmark-layer style={style} onClick={(e) => e.stopPropagation()} />
  );

  return (
    <Portal>
      {blocking && (
        <>
          {shade({ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) })}
          {shade({ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 })}
          {shade({ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height })}
          {shade({ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height })}
        </>
      )}
      {/* 스포트라이트 링 — 클릭은 통과 */}
      <Box
        position="fixed"
        zIndex={Z + 1}
        pointerEvents="none"
        borderRadius="10px"
        style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
        sx={{
          animation: `${pulse} 1.6s ease-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none", boxShadow: "0 0 0 4px #fff, 0 0 0 8px rgba(60,183,160,0.55)" },
        }}
      />
      {isMobile ? (
        <Box
          ref={sheetRef}
          role="dialog"
          aria-modal={blocking ? "true" : undefined}
          aria-label={title}
          position="fixed"
          left={0}
          right={0}
          {...(sheetAtTop ? { top: 0 } : { bottom: 0 })}
          zIndex={Z + 2}
          data-coachmark-layer
          // 모달 포커스 잠금(react-focus-lock)이 코치마크 버튼의 포커스를 모달로 되돌려 빼앗지 않게 — 키보드로 코치마크를 누를 수 있어야 한다
          data-no-focus-lock="true"
          bg="white"
          borderRadius={sheetAtTop ? "0 0 20px 20px" : "20px 20px 0 0"}
          px={5}
          pt={sheetAtTop ? 4 : 2.5}
          pb={sheetAtTop ? 4 : "max(20px, env(safe-area-inset-bottom))"}
          boxShadow="0 -12px 40px rgba(0,0,0,0.3)"
          sx={{ animation: `${rise} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) both`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
        >
          {!sheetAtTop && <Box w="40px" h="4px" borderRadius="2px" bg="neutral.300" mx="auto" mb={3} />}
          <Flex direction="column" gap={3}>
            <StepBadge index={stepIndex} count={stepCount} badge={badge} />
            <Text id={`${descId}-title`} fontSize="19px" fontWeight="800" lineHeight="1.35" letterSpacing="-0.01em" color="neutral.900" sx={{ wordBreak: "keep-all" }}>{title}</Text>
            <Text id={`${descId}-body`} fontSize="14px" lineHeight="1.6" color="neutral.700" sx={{ wordBreak: "keep-all" }}><Body body={body} strong={strong} /></Text>
            <Actions primary={primary} secondary={secondary} firstRef={firstRef} column />
          </Flex>
        </Box>
      ) : (
        <Box
          role="dialog"
          aria-modal={blocking ? "true" : undefined}
          aria-label={title}
          position="fixed"
          zIndex={Z + 2}
          data-coachmark-layer
          data-no-focus-lock="true" // 모달 포커스 잠금이 코치마크 버튼 포커스를 빼앗지 않게(위 시트와 같은 이유)
          w={`${TOOLTIP_W}px`}
          bg="white"
          borderRadius="14px"
          p="20px 20px 18px"
          boxShadow="0 20px 50px rgba(0,0,0,0.35)"
          style={{ top: tooltipStyle.top, bottom: tooltipStyle.bottom, left: tooltipStyle.left }}
          sx={{ animation: `${pop} 0.22s cubic-bezier(0.2, 0.8, 0.2, 1) both`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
        >
          <Box
            position="absolute"
            w={`${ARROW}px`}
            h={`${ARROW}px`}
            bg="white"
            borderRadius="2px"
            transform="rotate(45deg)"
            data-testid="coachmark-arrow"
            data-coachmark-arrow={tooltipStyle.arrow}
            style={tooltipStyle.arrow === "left"
              ? { left: -ARROW / 2, top: tooltipStyle.arrowOffset }
              : tooltipStyle.arrow === "top"
                ? { left: tooltipStyle.arrowOffset, top: -ARROW / 2 }
                : { left: tooltipStyle.arrowOffset, bottom: -ARROW / 2 }}
          />
          <Flex direction="column" gap={3}>
            <StepBadge index={stepIndex} count={stepCount} badge={badge} />
            <Text id={`${descId}-title`} fontSize="18px" fontWeight="800" lineHeight="1.35" letterSpacing="-0.01em" color="neutral.900" sx={{ wordBreak: "keep-all" }}>{title}</Text>
            <Text id={`${descId}-body`} fontSize="14px" lineHeight="1.6" color="neutral.700" sx={{ wordBreak: "keep-all" }}><Body body={body} strong={strong} /></Text>
            <Actions primary={primary} secondary={secondary} firstRef={firstRef} />
          </Flex>
        </Box>
      )}
    </Portal>
  );
}
